/* eslint-disable camelcase */
/* eslint no-param-reassign: "error" */
import {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Icon,
  Button,
  Spinner,
  NotFound,
  NavigationTabs,
  ProgramTemplate,
  ProgramWorkouts,
} from 'web-components';
import {
  usePageView,
} from 'web-hooks';

import {
  Flex,
  PrintLogo,
  TextBreadcrumbs,
} from 'web-styled';

const PersonalProgramsPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const location = useLocation();
  const id = Number(params.id);
  const resourceType = 'personalPrograms';

  const mapState = useCallback(state => ({
    program: coreSelectors.getResourceById(
      resourceType,
      id,
    )(state),
    clients: coreSelectors.getResourceMappedList(
      'clients',
    )(state),
    readStatus: getStatus(
      state,
      [
        `${resourceType}.requests.getProgram.status`,
        'programWeeks.requests.getProgramWeeks.status',
        'programWorkouts.requests.getProgramWorkouts.status',
        `${resourceType}.meta[${id}].readStatus`,
      ],
    ),
    isPro: coreSelectors.getProfile('is_pro')(state),
    updateStatus: getStatus(
      state,
      [
        `${resourceType}.meta[${id}].updateStatus`,
        'programWeeks.requests.addWeek.status',
        'programWeeks.requests.copyWeek.status',
        'programWorkouts.requests.createProgramWorkout.status',
        'programWorkouts.requests.copyWeekWorkouts.status',
      ],
    ),
  }), [id]);

  const {
    isPro,
    clients,
    program,
    readStatus,
    updateStatus,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestProgram(id, resourceType),
    );
    dispatch(
      coreActions.requestProgramWeeks(id, resourceType),
    );
    dispatch(
      coreActions.requestProgramWorkouts(id, resourceType),
    );
  }, [id]);

  usePageView({
    eventKey: 'viewed_personal_program',
    attrs: program,
    enabled: readStatus.succeeded && program && searchParams.get('is_new') !== '1',
  });

  const basePath = '/personal_programs';
  const isEdit = location.pathname?.includes('/edit');

  const tabs = [
    {
      name: i18n.t('workouts'),
      to: `${basePath}/${id}${isEdit ? '/edit' : ''}/workouts`,
    },
    {
      name: 'Overview',
      to: `${basePath}/${id}/${isEdit ? 'edit' : 'overview'}`,
    },
  ];

  const client = clients.find(c => c.id === program?.person_id);

  return (
    <Flex fillSpace column>
      <Flex
        flex="0 0 50px"
        width="100%"
        align="center"
        justify="space-between"
        padding="0px 15px"
      >
        <Flex align="center" spacing={10}>
          <Button
            appearance="link"
            onClick={() => {
              navigate(-1);
            }}
            noPrint
          >
            <Icon type="fa fa-chevron-left" />
          </Button>
          {client && (
            <TextBreadcrumbs
              chevron
              clickable
              onClick={() => {
                navigate(`/users/${client.id}/programs`);
              }}
            >
              {`${client.user_profile.first_name || ''} ${client.user_profile.last_name || ''}`}
            </TextBreadcrumbs>
          )}
          {program && (
            <TextBreadcrumbs active>
              {program.name}
            </TextBreadcrumbs>
          )}
        </Flex>
        <Flex noPrint align="center" spacing={10}>
          <NavigationTabs
            tabs={tabs}
            basePath={basePath}
          />
        </Flex>
        <PrintLogo />
      </Flex>
      {readStatus.succeeded && (
        <Routes>
          <Route
            index
            element={(
              <Navigate to="workouts" replace />
            )}
          />
          <Route
            path="overview"
            element={(
              <ProgramTemplate
                isPro={isPro}
                readOnly
                program={program}
                basePath={basePath}
                resourceType={resourceType}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <ProgramTemplate
                isPro={isPro}
                program={program}
                basePath={basePath}
                resourceType={resourceType}
              />
            )}
          />
          <Route
            path="workouts"
            element={(
              <ProgramWorkouts
                readOnly
                programId={id}
                resourceType={resourceType}
              />
            )}
          />
          <Route
            path="edit/workouts"
            element={(
              <ProgramWorkouts
                programId={id}
                resourceType={resourceType}
              />
            )}
          />
        </Routes>
      )}

      {readStatus.pending && (
        <Spinner message="Loading Program Template..." />
      )}

      {updateStatus.pending && (
        <Spinner over />
      )}

      {readStatus.failed && (
        <NotFound title={`Program Not Found (${id})`} />
      )}
    </Flex>
  );
};

export default PersonalProgramsPage;
